import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  RotateCcw,
  Save,
  Settings2,
} from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Alert, AlertDescription } from "components/ui/alert";
import { cn } from "lib/utils";

// Utilities
const getDimensionsKey = (questionsJson) => {
  if (!questionsJson) return null;
  if (Array.isArray(questionsJson.dimensions)) return "dimensions";
  if (Array.isArray(questionsJson.dimension)) return "dimension";
  return null;
};

const countAllQuestions = (questionsJson) => {
  const dimKey = getDimensionsKey(questionsJson);
  if (!dimKey) return 0;
  const dims = questionsJson[dimKey] || [];
  return dims.reduce((acc, dim) => {
    const factors = Array.isArray(dim.factors) ? dim.factors : [];
    return (
      acc +
      factors.reduce((fAcc, factor) => {
        const qs = Array.isArray(factor.questions) ? factor.questions : [];
        return fAcc + qs.length;
      }, 0)
    );
  }, 0);
};

export const flattenQuestions = (questionsJson) => {
  const dimKey = getDimensionsKey(questionsJson);
  if (!dimKey) return [];
  const dims = questionsJson[dimKey] || [];
  const flattened = [];
  dims.forEach((dimension, dIndex) => {
    const factors = Array.isArray(dimension.factors) ? dimension.factors : [];
    factors.forEach((factor, fIndex) => {
      const qs = Array.isArray(factor.questions) ? factor.questions : [];
      qs.forEach((q, qIndex) => {
        flattened.push({
          dIndex,
          fIndex,
          qIndex,
          id: Number(q?.id) || 0,
          questionText: q?.q || "",
          importance: typeof q?.importance === "number" ? q.importance : 0,
          role: q?.role ?? null,
          reverse: !!q?.reverse,
          dimensionTitle: dimension?.title || `Dimension ${dIndex + 1}`,
          factorTitle: factor?.title || `Factor ${fIndex + 1}`,
        });
      });
    });
  });
  flattened.sort((a, b) => a.id - b.id);
  return flattened;
};

export const applyOrderAndImportance = (questionsJson, orderedItems) => {
  if (!questionsJson) return questionsJson;
  const dimKey = getDimensionsKey(questionsJson);
  if (!dimKey) return questionsJson;

  // Deep clone to avoid mutating callers
  const result = JSON.parse(JSON.stringify(questionsJson));
  const dims = result[dimKey] || [];

  // Reassign sequential ids starting at 1, and write importance
  let nextId = 1;
  orderedItems.forEach((item) => {
    const { dIndex, fIndex, qIndex, importance } = item;
    if (
      dims?.[dIndex]?.factors?.[fIndex]?.questions?.[qIndex]
    ) {
      dims[dIndex].factors[fIndex].questions[qIndex].id = nextId;
      dims[dIndex].factors[fIndex].questions[qIndex].importance =
        typeof importance === "number" ? importance : 0;
      nextId += 1;
    }
  });

  // Ensure comments come after questions, with continuous order values
  const totalQs = nextId - 1;
  const comments = Array.isArray(result.comments) ? result.comments : [];
  // Stable by current order, fallback to index
  const safeNum = (v, def) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };
  const sortedComments = comments
    .map((c, idx) => ({ ...c, __idx: idx }))
    .sort((a, b) => safeNum(a.order, a.__idx) - safeNum(b.order, b.__idx));
  sortedComments.forEach((c, i) => {
    c.order = String(totalQs + i + 1);
  });
  result.comments = sortedComments.map(({ __idx, ...rest }) => rest);

  return result;
};

export const sortItemsByImportanceDesc = (items) => {
  const cloned = [...items];
  cloned.sort((a, b) => {
    const ai = typeof a.importance === "number" ? a.importance : 0;
    const bi = typeof b.importance === "number" ? b.importance : 0;
    if (bi !== ai) return bi - ai; // higher importance first
    // tie-breaker: original id ascending
    return a.id - b.id;
  });
  return cloned;
};

// UI Component
export const QuestionOrderManager = ({
  questions,
  onSave,
  onItemsChange,
  showInternalSave = true,
  header = "Reorder Questions",
}) => {
  const initialItems = useMemo(() => flattenQuestions(questions), [questions]);
  const [items, setItems] = useState(initialItems);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setItems(initialItems);
    setDirty(false);
    if (onItemsChange) onItemsChange(initialItems);
  }, [initialItems, onItemsChange]);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDirty(true);
    if (onItemsChange) onItemsChange(next);
  };

  const setImportance = (index, value) => {
    const next = [...items];
    const numeric = Number(value);
    next[index] = { ...next[index], importance: Number.isFinite(numeric) ? numeric : 0 };
    setItems(next);
    setDirty(true);
    if (onItemsChange) onItemsChange(next);
  };

  const resetOrder = () => {
    setItems(initialItems);
    setDirty(false);
    if (onItemsChange) onItemsChange(initialItems);
  };

  const sortByImportance = () => {
    const next = sortItemsByImportanceDesc(items);
    setItems(next);
    setDirty(true);
    if (onItemsChange) onItemsChange(next);
  };

  const handleSave = () => {
    const updated = applyOrderAndImportance(questions, items);
    onSave && onSave(updated);
    setDirty(false);
  };

  const totalQuestions = items.length;
  const totalComments = Array.isArray(questions?.comments) ? questions.comments.length : 0;

  return (
    <Card className="flex max-h-[80vh] flex-col border-border/70">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{header}</CardTitle>
        <CardDescription>
          Total questions: {totalQuestions} · Comments appended automatically: {totalComments}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 min-h-0 flex-col space-y-4 pt-0">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={sortByImportance}>
              <Settings2 className="mr-2 h-4 w-4" /> Sort by importance
            </Button>
            <Button variant="outline" size="sm" onClick={resetOrder}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset order
            </Button>
          </div>
          <div className="rounded-md bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
            Use the arrows to adjust ordering. Importance values help break ties when needed.
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden rounded-md border">
          <div className="h-full max-h-full overflow-y-auto divide-y divide-border/60">
            {items.map((item, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === items.length - 1;
              return (
                <div key={`${item.dIndex}-${item.fIndex}-${item.qIndex}`} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center">
                  <span className="w-10 text-sm font-semibold text-muted-foreground">{idx + 1}</span>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-foreground" title={item.questionText}>
                      {item.questionText}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.dimensionTitle} / {item.factorTitle} · Current id {item.id}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={item.importance}
                      onChange={(event) => setImportance(idx, event.target.value)}
                      className="w-24"
                      placeholder="Importance"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(idx, idx - 1)}
                      disabled={isFirst}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(idx, idx + 1)}
                      disabled={isLast}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {showInternalSave && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={resetOrder}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
            <Button onClick={handleSave} disabled={!dirty}>
              <Save className="mr-2 h-4 w-4" /> Save order
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionOrderManager;


